import { GetStaticProps } from 'next';
import destinations from 'scripts/cache/destinations.json';
import { getActivityPartnerById, getPartnerById } from '@/common/service/api/Partners/Partners';
import { PartnerDetail } from '@/screens/PartnersDetailPage/index';
import { TPartnerDetailProps } from '@/screens/PartnersDetailPage/PartnersDetail';
import { getRedirectsPath } from '@/common/service/api/Redirects/getRedirectsPath';
import { buildRootUrl } from '@/common/app/utils/urlsUtils';
import { getRecommends } from '@/shared/FeaturedActivities/service/ApiFeaturedActivities';
import {
    getCardImagesWithPlaceholders,
    getPlaceholders,
} from '@/common/service/api/Activity/Activity';

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking',
    };
}

type PageStaticProps = {
    state: string;
    destination: string;
    slug: string;
    partnerId: string;
};

export const getStaticProps: GetStaticProps<TPartnerDetailProps, PageStaticProps> = async ({
    params,
}) => {
    try {
        if (!params) {
            throw new Error(`PAGE PARTNER DETAILS: BAD PARAMS: ${params}`);
        }

        const item = await getPartnerById(params.partnerId);
        const currentPath = `/partners/${params.state}/${params.destination}/${params.slug}/${params.partnerId}/`;

        if (!item) {
            const redirectPath = await getRedirectsPath(currentPath);

            if (redirectPath === '/404/') {
                return {
                    notFound: true,
                };
            }

            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: redirectPath.toLowerCase(),
                    statusCode: 301,
                },
            };
        }

        const pageDestination =
            destinations.find((destination) => destination.id === item.destination_id) ||
            destinations[0];

        const rightPath =
            `/partners/${pageDestination.state.code}/${pageDestination.slug}/${item.slug}/${item.id}/`.toLowerCase();

        if (currentPath !== rightPath) {
            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: rightPath,
                    statusCode: 301,
                },
            };
        }

        const pageMeta = {
            title:
                item.meta.title ||
                `${item.title} in ${pageDestination.name || 'Tripshock'} (Activities & Photos)`,
            description:
                item.meta.description ||
                `Discover experiences from ${item.title} in ${
                    pageDestination.name || 'Tripshock'
                }. View photos, location, and BOOK online with discounted rates from Tripshock.com!`,
            canonical: buildRootUrl(rightPath),
        };

        const [fullList, recommends] = await Promise.all([
            getActivityPartnerById({
                partner_id: item.id,
                partner_slug: item.slug,
            }),
            getRecommends({
                destination_id: pageDestination.id,
            }),
        ]);

        const ids = fullList.items.map(({ id }) => id);

        recommends.forEach((i) => {
            if (!ids.includes(i.id)) {
                ids.push(i.id);
            }
        });

        const [images, placeholders] = await Promise.all([
            getCardImagesWithPlaceholders(ids),
            getPlaceholders(ids, 'activity'),
        ]);

        fullList.items.forEach((trip) => {
            trip.price = 0;
            trip.images = images[trip.id] || [];
            trip.placeholder = placeholders[trip.id] || '';
        });

        // const { items, activity_ids } = await getActivityPartnerById({
        //     partner_id: item.id,
        //     partner_slug: item.slug,
        // });

        // console.log('ITEMS !!!', items);

        const breadcrumbs = [
            {
                url: '/destination/',
                title: 'Destination',
            },
            {
                url: `/destination/${pageDestination.state.code.toLowerCase()}/`,
                title: pageDestination.state.name,
            },
            {
                url: `/destination/${pageDestination.state.code}/${pageDestination.slug}/`.toLowerCase(),
                title: pageDestination.name,
            },
            {
                url: `/partners/${pageDestination.state.code}/${pageDestination.slug}`.toLowerCase(),
                title: 'Tour Companies',
            },
            {
                title: item.title,
                url: rightPath,
                unlink: true,
            },
        ];

        return {
            props: {
                currentDestination: pageDestination,
                currentPartnerId: +params.partnerId,
                item: item,
                fullList: {
                    ...fullList,
                    meta: pageMeta,
                },
                placeholders,
                recommendedTrips: recommends,
                breadcrumbs,
                recommendedIds: recommends.map(({ id }) => id),
            },
        };
    } catch (e) {
        throw new Error((e as Error).message);
    }
};

export default PartnerDetail;
